import React, { ReactNode } from 'react';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';
import { Formatter } from 'recharts/types/component/DefaultTooltipContent';
import { AxisInterval } from 'recharts/types/util/types';
import { Paper, useTheme } from '@mui/material';
import { OverlaySpinner } from '@ankr.com/ui';

import { Title } from '../Title';
import { UsageHistoryDataMapped } from '../../types';
import { useBarChartWidgetStyles } from './BarChartWidgetStyles';
import { monthLabelFormatter } from './utils/monthLabelFormatter';
import { requestsValueFormatter } from './utils/requestsValueFormatter';
import { useContent } from '../RequestsChart/hooks/useContent';

interface IBarChartWidgetProps {
  NoDataPlaceholder: () => JSX.Element;
  className?: string;
  csvDownloadButton?: ReactNode;
  data?: UsageHistoryDataMapped[];
  isLightTheme: boolean;
  isLoading: boolean;
  title?: string;
  tooltipValueFormatter?: Formatter<string, string>;
  yAxisTickDx?: number;
  yAxisTickFormatter: (value: number) => string;
  xAxisInterval?: AxisInterval;
}

const Y_AXIS_MAX_VALUE_COEFFICIENT = 1.2;

export const BarChartWidget = ({
  NoDataPlaceholder,
  className,
  csvDownloadButton,
  data,
  isLightTheme,
  isLoading,
  title,
  tooltipValueFormatter = requestsValueFormatter,
  yAxisTickDx = -5,
  yAxisTickFormatter,
  xAxisInterval = 0,
}: IBarChartWidgetProps) => {
  const { classes, cx } = useBarChartWidgetStyles();

  const { hasPreloader, hasChart, hasPlaceholder } = useContent({
    data,
    isLoading,
  });

  const theme = useTheme();

  return (
    <Paper className={cx(classes.root, className)}>
      {title && (
        <Title className={classes.title} csvButton={csvDownloadButton}>
          {title}
        </Title>
      )}
      {hasPreloader && <OverlaySpinner />}
      {hasPlaceholder && <NoDataPlaceholder />}
      {hasChart && (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid
              strokeDasharray="5 5"
              strokeWidth={0.3}
              horizontal
              vertical
              style={{ stroke: theme.palette.grey[100] }}
            />
            <XAxis
              axisLine={false}
              tickLine={false}
              dataKey="month"
              tick={{ fill: theme.palette.text.secondary, dy: 10 }}
              fontSize={12}
              tickSize={0}
              interval={xAxisInterval}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={yAxisTickFormatter}
              fontSize={12}
              tick={{ fill: theme.palette.text.secondary, dx: yAxisTickDx }}
              tickSize={0}
              width={35}
              domain={[
                'auto',
                (dataMax: number) => dataMax * Y_AXIS_MAX_VALUE_COEFFICIENT,
              ]}
            />
            <Bar dataKey="calls" fill="#356DF3">
              <LabelList
                dataKey="formattedCallsValue"
                position="top"
                offset={8}
                fill={isLightTheme ? 'black' : 'white'}
                fontSize={12}
              />
            </Bar>
            <Tooltip
              contentStyle={{ fontSize: 12, color: 'black' }}
              labelFormatter={monthLabelFormatter}
              formatter={tooltipValueFormatter}
              cursor={false}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};
